import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import askAi from '../../assets/alison-run/ask-ai-off.svg';
import stepCheck from '../../assets/alison-run/step-check.svg';
import { useShellAccount } from '../../lib/useShellAccount.js';
import { AppShell } from '../AppShell.js';
import {
  ALISON_PROGRESS_GRADIENT,
  ALISON_SELECTED,
  ALISON_TEXT_SUBTLE,
  ALISON_TEXT_TITLE,
} from '../workflows/alison-run/ui/alisonRunColors.js';

// Inside the app's own shell (rail, TopBar) with the rail's nav hidden: a client in
// onboarding has nowhere else to go yet, and the onboarding gate would bring it back.
const COLUMN_WIDTH = 720;
const STEPS = [
  { key: 'account', label: 'Account' },
  { key: 'networks', label: 'Ad networks' },
  { key: 'setup', label: 'Setup' },
] as const;

export type OnboardingStep = 'networks' | 'setup';

export interface OnboardingShellProps {
  email: string | null;
  onSignOut: () => void;
  step: OnboardingStep;
  title: string;
  /** Under the title: what this step is for, in a sentence. */
  subtitle?: string;
  /** Beside the action: what happens next, in a sentence. */
  note: string;
  /** The step's call to action. */
  action?: ReactNode;
  children: ReactNode;
}

function StepTrail({ step }: { step: OnboardingStep }): ReactNode {
  const current = STEPS.findIndex((entry) => entry.key === step);
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Stack
        component="ol"
        direction="row"
        aria-label="Setup steps"
        sx={{ listStyle: 'none', m: 0, p: 0, gap: 2, alignItems: 'center' }}
      >
        {STEPS.map((entry, index) => {
          const done = index < current;
          const now = index === current;
          return (
            <Stack
              component="li"
              key={entry.key}
              direction="row"
              aria-current={now ? 'step' : undefined}
              sx={{
                alignItems: 'center',
                gap: 0.75,
                fontSize: 11.5,
                fontWeight: now ? 700 : 500,
                color: now ? 'primary.main' : done ? 'text.primary' : ALISON_TEXT_SUBTLE,
                ...(now && { bgcolor: ALISON_SELECTED, borderRadius: '6px', px: 1, py: 0.25 }),
              }}
            >
              {done ? (
                <Box component="img" src={stepCheck} alt="" sx={{ width: 12, height: 12 }} />
              ) : now ? (
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
              ) : null}
              {entry.label}
            </Stack>
          );
        })}
      </Stack>
      <Box
        role="progressbar"
        aria-label="Setup progress"
        aria-valuenow={current + 1}
        aria-valuemin={0}
        aria-valuemax={STEPS.length}
        sx={{ height: 4, borderRadius: 999, bgcolor: 'background.muted', overflow: 'hidden' }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${((current + 1) / STEPS.length) * 100}%`,
            background: ALISON_PROGRESS_GRADIENT,
            borderRadius: 999,
          }}
        />
      </Box>
    </Stack>
  );
}

export function OnboardingShell({
  email,
  onSignOut,
  step,
  title,
  subtitle,
  note,
  action,
  children,
}: OnboardingShellProps): ReactNode {
  const account = useShellAccount(email ?? undefined);
  return (
    <AppShell account={account} onLogout={onSignOut} active="dashboard" hideNav>
      <Stack sx={{ width: '100%', maxWidth: COLUMN_WIDTH, mx: 'auto', px: 3, py: 5, gap: 3 }}>
        <StepTrail step={step} />
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
          <Box component="img" src={askAi} alt="" sx={{ width: 32, height: 32, flexShrink: 0 }} />
          <Stack sx={{ gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{ fontSize: 24, fontWeight: 500, lineHeight: '32px', color: ALISON_TEXT_TITLE }}
            >
              {title}
            </Typography>
            {subtitle !== undefined && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{subtitle}</Typography>
            )}
          </Stack>
        </Stack>
        {children}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, color: ALISON_TEXT_SUBTLE }}>
            {note}
          </Typography>
          {action}
        </Stack>
      </Stack>
    </AppShell>
  );
}
